import { inject } from '@angular/core';
import { BackendService } from './backend.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Id, NullableId, Paginated, Params } from '@feathersjs/feathers';

type RemovedUnderscoreFields<T> = {
    [K in keyof T as K extends `_${string}` ? never : K]: T[K];
};

function removeVirtualFields<TYPE>(obj: TYPE): RemovedUnderscoreFields<TYPE> {
    const objectCopy = Object.assign({}, obj);
    for (const key in objectCopy) {
        if (Object.prototype.hasOwnProperty.call(objectCopy, key) && key.startsWith('_')) {
            delete objectCopy[key];
        }
    }

    return objectCopy;
}

export class ApiService<T> {
    protected readonly backendService: BackendService = inject(BackendService);
    public itemSubject: BehaviorSubject<T> = new BehaviorSubject(null);
    public itemObservable: Observable<T> = this.itemSubject.asObservable();
    public items: any = [];
    public itemsLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public itemsLoadedObservable: Observable<boolean> = this.itemsLoaded.asObservable();
    public itemsSubject: BehaviorSubject<T[]> = new BehaviorSubject([]);
    public itemsObservable: Observable<T[]> = this.itemsSubject.asObservable();
    protected serviceName: string;
    public service: any;
    protected isLoading: boolean = false;
    protected loadDebounce: any;

    public initConnection() {
        if (this.serviceName) {
            this.service = this.backendService.getService(this.serviceName);
            this.service.on('created', (item: T) => this._created(item));
            this.service.on('updated', (item: T) => this._updated(item));
            this.service.on('patched', (item: T) => this._updated(item));
            this.service.on('removed', (item: T) => this._removed(item));
        }
    }

    public get(id: Id, params?: Params): Promise<T> {
        this.service = this.checkSocket();
        // return this.service.get(id, params);
        return this.service
            .get(id, params)
            .then((result: T) => {
                // console.log(`API service.${this.serviceName}.GET ok `, id, params, result);
                return result;
            })
            .catch((error) => {
                console.error(`API service.${this.serviceName}.GET error `, id, params, error);
                throw error;
            });
    }

    public find(params?: Params): Promise<T | T[] | Paginated<T>> {
        this.service = this.checkSocket();
        // return this.service.find(params);
        return this.service
            .find(params)
            .then((result: T) => {
                // console.log(`API service.${this.serviceName}.FIND ok `, params, result);
                return result;
            })
            .catch((error) => {
                console.error(`API service.${this.serviceName}.FIND error `, params, error);
                throw error;
            });
    }

    public create(data: Partial<T>, params?: Params): Promise<T> {
        // console.log(`API service.${this.serviceName}.create`, data, params);
        this.service = this.checkSocket();
        return this.service.create(data, params);
    }

    public patch(id: Id, data: Partial<T>, params?: Params): Promise<T> {
        this.service = this.checkSocket();
        // return this.service.patch(id, removeVirtualFields(data), params);
        return this.service
            .patch(id, removeVirtualFields(data), params)
            .then((result: T) => {
                // console.log(`API service.${this.serviceName}.PATCH ok `, id, params, result);
                return result;
            })
            .catch((error) => {
                console.error(`API service.${this.serviceName}.PATCH error `, id, params, error);
                throw error;
            });
    }

    public update(id: NullableId, data: T, params?: Params): Promise<T> {
        this.service = this.checkSocket();
        return this.service.update(id, removeVirtualFields(data), params);
    }

    public remove(id: Id, params?: Params): Promise<T> {
        this.service = this.checkSocket();
        return this.service.remove(id, params);
    }

    public setItems(data: T[]): void {
        this.items = data;
        this.itemsSubject.next(this.items);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected _created(item: T): void {}

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected _updated(item: T): void {}

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected _removed(item: T): void {}

    public checkSocket() {
        if (!this.service) {
            this.initConnection();
        }
        return this.service;
    }
}
